/**
 * @file auth.reducer.js
 * @memberof Auth
 * @description Reducer for managing authentication-related state in the Redux store.
 */

import {
  LOGIN_USER,
  LOGIN_USER_SUCCESS,
  LOGIN_USER_ERROR,
  REGISTER_USER,
  REGISTER_USER_SUCCESS,
  REGISTER_USER_ERROR,
  LOGOUT_USER,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_ERROR,
  RESET_PASSWORD,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_ERROR,
} from "../actions";

import {
  UPDATE_USER_PERMISSIONS,
  REFRESH_PERMISSIONS,
  REFRESH_PERMISSIONS_SUCCESS,
  REFRESH_PERMISSIONS_ERROR
} from './types';

// Helper function to determine role from isAdmin and isClientAdmin flags
const determineRole = (isAdmin, isClientAdmin) => {
  // Check for various truthy representations: "true", true, "1", 1
  if (isAdmin === "true" || isAdmin === true || isAdmin === "1" || isAdmin === 1) return "Admin";
  if (isClientAdmin === "true" || isClientAdmin === true || isClientAdmin === "1" || isClientAdmin === 1) return "Client Admin";
  return "Regular User";
};

// Initialize role from localStorage
const initializeRole = () => {
  const isAdmin = localStorage.getItem("isAdmin");
  const isClientAdmin = localStorage.getItem("isClientAdmin");
  return determineRole(isAdmin, isClientAdmin);
};

const INIT_STATE = {
  user: localStorage.getItem("user_id") || null,
  userId: localStorage.getItem("user_id") || null,
  clientId: localStorage.getItem("client_id") || null,
  role: initializeRole(), // Initialize role from localStorage
  permissions: {}, // Additional granular permissions if needed
  forgotUserMail: "",
  newPassword: "",
  resetPasswordCode: "",
  loading: false,
  error: "",
  permissionsLoading: false,
  permissionsError: null
};

/**
 * Auth reducer function
 * @param {Object} state - Current state
 * @param {Object} action - Dispatched action
 * @returns {Object} New state
 */
export default (state = INIT_STATE, action) => {
  switch (action.type) {
    case LOGIN_USER:
      return { ...state, loading: true, error: "" };
    case LOGIN_USER_SUCCESS:
      const { userId, client_id, isAdmin, isClientAdmin } = action.payload;
      return { 
        ...state, 
        loading: false, 
        user: userId, 
        userId: userId,
        clientId: client_id,
        role: determineRole(isAdmin, isClientAdmin),
        error: "" 
      };
    case LOGIN_USER_ERROR:
      return {
        ...state,
        loading: false,
        user: "",
        error: action.payload.message,
      };
    case FORGOT_PASSWORD:
      return { ...state, loading: true, error: "" };
    case FORGOT_PASSWORD_SUCCESS:
      return {
        ...state,
        loading: false,
        forgotUserMail: action.payload,
        error: "",
      };
    case FORGOT_PASSWORD_ERROR:
      return {
        ...state,
        loading: false,
        forgotUserMail: "",
        error: action.payload.message,
      };
    case RESET_PASSWORD:
      return { ...state, loading: true, error: "" };
    case RESET_PASSWORD_SUCCESS:
      return {
        ...state,
        loading: false,
        newPassword: action.payload,
        resetPasswordCode: "",
        error: "",
      };
    case RESET_PASSWORD_ERROR:
      return {
        ...state,
        loading: false,
        newPassword: "",
        resetPasswordCode: "",
        error: action.payload.message,
      };
    case REGISTER_USER:
      return { ...state, loading: true, error: "" };
    case REGISTER_USER_SUCCESS:
      return { ...state, loading: false, user: action.payload.uid, error: "" };
    case REGISTER_USER_ERROR:
      return {
        ...state,
        loading: false,
        user: "",
        error: action.payload.message,
      };
    case LOGOUT_USER:
      return { 
        ...state, 
        user: null, 
        userId: null,
        clientId: null,
        role: null,
        permissions: {},
        error: "" 
      };
    
    case UPDATE_USER_PERMISSIONS:
      return {
        ...state,
        role: action.payload.role || state.role,
        permissions: action.payload.permissions || state.permissions
      };
    
    case REFRESH_PERMISSIONS:
      return { ...state, permissionsLoading: true, permissionsError: null };
    
    case REFRESH_PERMISSIONS_SUCCESS:
      return {
        ...state,
        permissionsLoading: false,
        role: determineRole(action.payload.isAdmin, action.payload.isClientAdmin),
        permissions: action.payload.permissions || {},
        permissionsError: null
      };
    
    case REFRESH_PERMISSIONS_ERROR:
      return {
        ...state,
        permissionsLoading: false,
        permissionsError: action.payload
      };
    default:
      return { ...state };
  }
};
